import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useConfig } from "@/hooks/useConfig";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Clock, Upload, ArrowLeft } from "lucide-react";
import { statusLabel, STATUS_COLORS, money, when, dayOnly, pretty, eventHeadline,
  compressPhoto, isPhotoFile, sizeText, PHOTO_HARD_LIMIT, VIDEO_LIMIT, EMAIL_NOT_SENT_TEXT } from "./claimsShared";

export default function ClaimDetailPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const liabilityParties = getValues("claim_liability_parties");
  const rejectReasons = getValues("claim_reject_reasons");

  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [comment, setComment] = useState("");
  const [viewer, setViewer] = useState(null);

  const load = useCallback(() => {
    api.get(`/claims/${claimId}`).then((r) => setClaim(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not open the claim"));
  }, [claimId]);
  useEffect(() => { load(); }, [load]);

  const open = (kind, preset = {}) => { setForm(preset); setDialog(kind); };

  const call = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); setDialog(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "That did not work"); }
    finally { setBusy(false); }
  };

  if (!claim) return <div className="text-sm text-zinc-500" data-testid="claim-detail-loading">Loading claim…</div>;

  const can = claim.can || {};
  const st = claim.status;
  const breaches = claim.sla?.breaches || [];
  const rd = claim.reviewer_decision;
  const fd = claim.final_decision;

  const uploadEvidence = async (file) => {
    try {
      let toSend = file;
      if (isPhotoFile(file)) {
        toSend = await compressPhoto(file);
        if (toSend.size > PHOTO_HARD_LIMIT) {
          toast.error(`${file.name} is still ${sizeText(toSend.size)}. A photo must be under 2 MB. Please take the photo again.`);
          return;
        }
      } else if (file.size > VIDEO_LIMIT) {
        toast.error(`${file.name} is ${sizeText(file.size)}. A video must be under 50 MB.`);
        return;
      }
      const fd2 = new FormData();
      fd2.append("file", toSend, file.name);
      await api.post(`/claims/${claim._id}/evidence`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} added (${sizeText(toSend.size)})`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add that file"); }
  };

  const decisionBody = () => ({
    decision: form.decision,
    note: form.note || "",
    reason_code: form.reason_code || "",
    liability_party: form.liability_party || "",
    liability_party_name: form.liability_party_name || "",
    ...(form.mode === "amount" ? { approved_amount: Number(form.approved_amount || 0) }
      : { lines: (claim.lines || []).map((l) => ({ line_id: l.line_id, qty_approved: Number(form.qty?.[l.line_id] ?? l.qty_claimed) })) }),
  });

  return (
    <div className="space-y-5 max-w-6xl" data-testid="claim-detail-page">
      <button onClick={() => navigate("/claims")} className="inline-flex items-center gap-1 text-xs text-zinc-500" data-testid="back-to-claims">
        <ArrowLeft className="w-3 h-3" /> All claims
      </button>

      {/* HEADER */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="claim-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-zinc-900 font-mono">{claim.claim_id}</h1>
            <p className="text-sm text-zinc-600">{claim.customer_name} · {claim.channel || "—"} · order {claim.order_number}</p>
            <p className="text-xs text-zinc-500 mt-1">
              {pretty(claim.claim_type)} · {pretty(claim.priority)} priority · goods received {dayOnly(claim.goods_received_at)} ·
              complaint {when(claim.complaint_received_at)}
            </p>
          </div>
          <div className="text-right space-y-1">
            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[st] || "bg-zinc-100"}`} data-testid="claim-status-badge">
              {statusLabel(st)}
            </span>
            <p className="text-sm font-semibold text-zinc-900" data-testid="claim-values">
              Claim Amount {money(claim.claimed_value)}{claim.approved_value ? ` · Approved Amount ${money(claim.approved_value)}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {claim.is_late && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold" data-testid="claim-late-badge">Late</span>}
          {claim.evidence_late && <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[11px] font-semibold" data-testid="claim-evidence-late-badge">Evidence late</span>}
          {claim.email_not_sent && (
            <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[11px] font-semibold" data-testid="claim-email-not-sent-badge">Email not sent</span>
          )}
          {claim.requires_final_approval && <span className="px-2 py-0.5 rounded bg-violet-100 text-violet-700 text-[11px] font-semibold" data-testid="claim-needs-final-badge">Admin must decide</span>}
          {breaches.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 text-[11px] font-semibold" data-testid="claim-sla-breach-badge">
              <AlertTriangle className="w-3 h-3" /> SLA breached{breaches.some((b) => b.escalated) ? " — ESCALATED" : ""}
            </span>
          )}
          {claim.sla?.current_stage_due_at && breaches.length === 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid="claim-sla-clock">
              <Clock className="w-3 h-3" /> {pretty(claim.sla.current_stage)} due {when(claim.sla.current_stage_due_at)}
            </span>
          )}
        </div>
        {claim.is_late && claim.late_reason && (
          <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="claim-late-reason">
            <strong>Late reason:</strong> {claim.late_reason}
          </p>
        )}
        {claim.email_not_sent && (
          <p className="text-xs bg-orange-50 border border-orange-300 text-orange-800 rounded p-2 font-semibold" data-testid="claim-email-not-sent-warning">
            {EMAIL_NOT_SENT_TEXT}
          </p>
        )}
        {claim.cn_rejection_reason && (
          <p className="text-xs bg-red-50 border border-red-200 rounded p-2" data-testid="claim-cn-rejection">
            <strong>Credit note rejected:</strong> {claim.cn_rejection_reason}
          </p>
        )}
        {claim.description && <p className="text-xs text-zinc-600" data-testid="claim-description-text">{claim.description}</p>}
      </div>

      {/* ACTIONS */}
      <div className="flex flex-wrap gap-2" data-testid="claim-actions">
        {can.override_late && st === "late_pending_admin" && (
          <>
            <Button size="sm" onClick={() => open("late", { decision: "allow" })} data-testid="late-allow-btn">Allow</Button>
            <Button size="sm" variant="outline" onClick={() => open("late", { decision: "reject" })} data-testid="late-reject-btn">Reject</Button>
          </>
        )}
        {can.review && ["submitted", "under_review"].includes(st) && (
          <Button size="sm" variant="outline" onClick={() => open("video")} data-testid="request-video-call-btn">Ask Video Call</Button>
        )}
        {st === "video_call_requested" && (can.is_raiser || can.review) && (
          <Button size="sm" variant="outline" onClick={() => open("videodone")} data-testid="video-call-done-btn">Video Call Done</Button>
        )}
        {can.review && ["submitted", "under_review", "video_call_requested"].includes(st) && (
          <Button size="sm" onClick={() => open("reviewer", { decision: "approve", mode: "lines", qty: {} })} data-testid="reviewer-decide-btn">
            {claim.requires_final_approval ? "Give Decision & Send to Admin" : "Give Decision"}
          </Button>
        )}
        {can.final_approve && ["pending_final_approval", "more_info_requested"].includes(st) && (
          <>
            <Button size="sm" onClick={() => open("final", { decision: "approve", mode: "lines", qty: {} })} data-testid="final-decide-btn">Admin Decision</Button>
            <Button size="sm" variant="outline" onClick={() => open("moreinfo")} data-testid="ask-more-info-btn">Ask More Proof</Button>
          </>
        )}
        {can.review && st === "more_info_requested" && (
          <Button size="sm" onClick={() => open("resubmit")} data-testid="resubmit-admin-btn">Send Back to Admin</Button>
        )}
        {can.create_cn && st === "cn_pending" && (
          <Button size="sm" onClick={() => open("cn", { amount: claim.approved_value })} data-testid="create-cn-btn">Make CN</Button>
        )}
        {can.send_cn && (st === "cn_approved" || claim.email_not_sent) && (
          <>
            <Button size="sm" onClick={() => open("emailcn")} data-testid="email-cn-btn">Send CN to Customer</Button>
            <Button size="sm" variant="outline" onClick={() => open("marksent")} data-testid="mark-cn-sent-btn">Sent by Hand</Button>
          </>
        )}
        {can.reopen && st === "closed_rejected" && (
          <Button size="sm" variant="outline" onClick={() => open("reopen")} data-testid="reopen-claim-btn">Open Again</Button>
        )}
        {!["closed_settled", "closed_rejected"].includes(st) && (
          <label className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 cursor-pointer px-2">
            <Upload className="w-4 h-4" /> Add Photo or Video
            <input type="file" className="hidden" data-testid="detail-evidence-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadEvidence(f); e.target.value = ""; }} />
          </label>
        )}
      </div>

      {/* EVIDENCE */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="claim-evidence-gallery">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Photos &amp; Videos ({(claim.evidence || []).length})</h2>
        <div className="flex gap-3 flex-wrap">
          {(claim.evidence || []).length === 0 && <span className="text-xs text-zinc-400">Nothing added yet.</span>}
          {(claim.evidence || []).map((e) => (
            <div key={e.file_id} className="w-40 border border-zinc-200 rounded-lg overflow-hidden" data-testid={`gallery-${e.kind}`}>
              {e.kind === "photo" && (
                <img src={e.file_url} alt={e.file_name} loading="lazy" className="w-full h-24 object-cover cursor-zoom-in"
                  onClick={() => setViewer(e)} data-testid={`gallery-photo-${e.file_id}`} />
              )}
              {e.kind === "video" && (
                <video src={e.file_url} controls preload="none" className="w-full h-24 bg-black" data-testid={`gallery-video-${e.file_id}`} />
              )}
              {e.kind === "report" && <a href={e.file_url} className="block h-24 text-xs flex items-center justify-center text-teal-700 underline" data-testid={`gallery-report-${e.file_id}`}>Open report</a>}
              <div className="px-2 py-1 text-[10px] truncate text-zinc-500">{e.file_name}</div>
              <div className="px-2 pb-1 text-[10px] text-zinc-400" data-testid={`gallery-size-${e.file_id}`}>{sizeText(e.size)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* LINES */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 overflow-x-auto" data-testid="claim-lines-table">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Lines</h2>        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>{["SKU", "Batch", "Invoiced", "Claim Qty", "Rate", "Claim Amount", "Approved Qty", "Approved Amount"].map((h) => (
              <th key={h} className="text-left px-2 py-2 font-semibold">{h}</th>))}</tr>
          </thead>
          <tbody>
            {(claim.lines || []).map((l) => (
              <tr key={l.line_id} className="border-t border-zinc-100" data-testid={`claim-line-${l.sku_id}`}>
                <td className="px-2 py-2 font-medium">{l.sku_name}</td>
                <td className="px-2 py-2 font-mono">{l.batch_number}{!l.batch_on_order && <span className="ml-1 text-[10px] text-amber-700">batch not on order</span>}</td>
                <td className="px-2 py-2">{l.qty_invoiced}</td>
                <td className="px-2 py-2">{l.qty_claimed}</td>
                <td className="px-2 py-2">{money(l.rate)}</td>
                <td className="px-2 py-2">{money(l.value)}</td>
                <td className="px-2 py-2">{l.qty_approved || 0}</td>
                <td className="px-2 py-2 font-medium">{money(l.value_approved)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(rd || fd) && (
          <div className="mt-3 text-xs space-y-1" data-testid="claim-decisions-summary">
            {rd && <p data-testid="claim-preliminary-summary"><strong>{rd.is_preliminary ? "Preliminary" : "Reviewer"} decision:</strong> {pretty(rd.decision)} by {rd.by_name} — {money((rd.approved_value_paise || 0) / 100)} {rd.liability_party ? `· who pays: ${rd.liability_party}` : ""}</p>}
            {fd && <p data-testid="claim-final-summary"><strong>Admin Decision:</strong> {pretty(fd.decision)} by {fd.by_name} — {money((fd.approved_value_paise || 0) / 100)} {fd.liability_party ? `· who pays: ${fd.liability_party}` : ""}</p>}
          </div>
        )}
      </div>

      {/* RECOVERIES */}
      {(claim.recoveries || []).length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="claim-recoveries">
          <h2 className="text-base font-semibold text-zinc-900 mb-2">Money to Get Back</h2>
          {claim.recoveries.map((r) => (
            <p key={r.recovery_id} className="text-xs" data-testid={`claim-recovery-${r.recovery_id}`}>
              {r.recovery_id} · {r.party_name} ({r.party_type}) · {money(r.amount)} · {pretty(r.status)}
            </p>
          ))}
        </div>
      )}

      {/* COMMENT + TIMELINE */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="claim-timeline">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Timeline</h2>
        <div className="flex gap-2 mb-4">
          <Input className="h-8 text-xs" placeholder="Add a comment…" value={comment}
            onChange={(e) => setComment(e.target.value)} data-testid="claim-comment-input" />
          <Button size="sm" variant="outline" disabled={!comment}
            onClick={() => call(() => api.post(`/claims/${claim._id}/comment`, { note: comment }).then(() => setComment("")), "Comment added")}
            data-testid="claim-comment-btn">Post</Button>
        </div>
        <ol className="space-y-2">
          {(claim.events || []).map((ev) => (
            <li key={ev._id} className="border-l-2 border-zinc-200 pl-3" data-testid={`timeline-${ev.type}`}>
              <p className="text-xs font-semibold text-zinc-900">{eventHeadline(ev)}</p>
              {ev.note && <p className="text-xs text-zinc-600" data-testid={`timeline-note-${ev.type}`}>{ev.note}</p>}
              <p className="text-[10px] text-zinc-400">{when(ev.at)}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* IMAGE VIEWER */}
      <Dialog open={!!viewer} onOpenChange={() => setViewer(null)}>
        <DialogContent className="max-w-3xl" data-testid="evidence-viewer">
          <DialogHeader><DialogTitle className="text-base">{viewer?.file_name}</DialogTitle></DialogHeader>
          {viewer && <img src={viewer.file_url} alt={viewer.file_name} className="w-full rounded" />}
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOGS ═══ */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-xl" data-testid={`claim-dialog-${dialog}`}>
          <DialogHeader>
            <DialogTitle className="text-base">
              {dialog === "late" && (form.decision === "allow" ? "Allow this late claim" : "Reject this late claim")}
              {dialog === "video" && "Ask Video Call"}
              {dialog === "videodone" && "Video Call Done"}
              {dialog === "reviewer" && "Give Decision"}
              {dialog === "final" && "Admin Decision"}
              {dialog === "moreinfo" && "Ask More Proof"}
              {dialog === "resubmit" && "Send Back to Admin"}
              {dialog === "cn" && "Make CN"}
              {dialog === "emailcn" && "Send CN to Customer"}
              {dialog === "marksent" && "Sent by Hand"}
              {dialog === "reopen" && "Open this claim again"}
            </DialogTitle>
          </DialogHeader>

          {["reviewer", "final"].includes(dialog) && (
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                {["approve", "partly_approve", "reject"].map((d) => (
                  <button key={d} onClick={() => setForm((f) => ({ ...f, decision: d }))}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${form.decision === d ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}
                    data-testid={`decision-${d}`}>{pretty(d)}</button>
                ))}
              </div>
              {form.decision === "partly_approve" && (
                <>
                  <div className="flex gap-2">
                    {["lines", "amount"].map((m) => (
                      <button key={m} onClick={() => setForm((f) => ({ ...f, mode: m }))}
                        className={`px-2 py-0.5 rounded text-[11px] ${form.mode === m ? "bg-teal-600 text-white" : "bg-zinc-100"}`}
                        data-testid={`partial-mode-${m}`}>{m === "lines" ? "Per line" : "By amount"}</button>
                    ))}
                  </div>
                  {form.mode === "amount" ? (
                    <div>
                      <Label className="text-xs">Approved amount (₹, not more than {money(claim.claimed_value)})</Label>
                      <Input type="number" className="h-8 text-xs" value={form.approved_amount || ""}
                        onChange={(e) => setForm((f) => ({ ...f, approved_amount: e.target.value }))} data-testid="approved-amount-input" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {(claim.lines || []).map((l) => (
                        <div key={l.line_id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1">{l.sku_name} ({l.batch_number}) — claimed {l.qty_claimed}</span>
                          <Input type="number" className="h-7 w-20 text-xs"
                            value={form.qty?.[l.line_id] ?? ""} placeholder={String(l.qty_claimed)}
                            onChange={(e) => setForm((f) => ({ ...f, qty: { ...f.qty, [l.line_id]: e.target.value } }))}
                            data-testid={`approve-qty-${l.sku_id}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {form.decision !== "reject" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Who Pays?</Label>
                    <select className="w-full h-8 border border-zinc-200 rounded px-1 text-xs" value={form.liability_party || ""}
                      onChange={(e) => setForm((f) => ({ ...f, liability_party: e.target.value }))} data-testid="liability-select">
                      <option value="">Select…</option>
                      {(liabilityParties.length ? liabilityParties : ["SKIPPI", "Brand", "Transporter", "Warehouse", "Customer"]).map((p) => (
                        <option key={p} value={p}>{p}</option>))}
                    </select>
                  </div>
                  {["Transporter", "Warehouse"].includes(form.liability_party) && (
                    <div>
                      <Label className="text-xs">{form.liability_party} name</Label>
                      <Input className="h-8 text-xs" value={form.liability_party_name || ""}
                        onChange={(e) => setForm((f) => ({ ...f, liability_party_name: e.target.value }))} data-testid="liability-name-input" />
                    </div>
                  )}
                </div>
              )}
              {form.decision === "reject" && (
                <div>
                  <Label className="text-xs">Rejection reason</Label>
                  <select className="w-full h-8 border border-zinc-200 rounded px-1 text-xs" value={form.reason_code || ""}
                    onChange={(e) => setForm((f) => ({ ...f, reason_code: e.target.value }))} data-testid="reject-reason-select">
                    <option value="">Select…</option>
                    {rejectReasons.map((r) => (<option key={r} value={r}>{r}</option>))}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs">Note</Label>
                <Textarea rows={2} className="text-xs" value={form.note || ""}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="decision-note" />
              </div>
            </div>
          )}

          {dialog === "cn" && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-600">
                The credit note is created in the existing Credit Notes module as <strong>Pending Approval</strong>.
                Admin still approves it there.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tally CN number</Label>
                  <Input className="h-8 text-xs" value={form.cn_number || ""}
                    onChange={(e) => setForm((f) => ({ ...f, cn_number: e.target.value }))} data-testid="cn-number-input" />
                </div>
                <div>
                  <Label className="text-xs">CN date</Label>
                  <Input type="date" className="h-8 text-xs" value={form.cn_date || ""}
                    onChange={(e) => setForm((f) => ({ ...f, cn_date: e.target.value }))} data-testid="cn-date-input" />
                </div>
                <div>
              <Label className="text-xs">Amount (₹) — Approved Amount {money(claim.approved_value)}, you may only lower it</Label>
                  <Input type="number" className="h-8 text-xs" value={form.amount ?? claim.approved_value}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="cn-amount-input" />
                </div>
                <div>
                  <Label className="text-xs">CN PDF (required)</Label>
                  <Input type="file" accept="application/pdf" className="h-8 text-xs" data-testid="cn-pdf-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = () => setForm((s) => ({
                        ...s, attachments: [{ filename: f.name, content_type: "application/pdf", data: String(reader.result).split(",")[1] }],
                      }));
                      reader.readAsDataURL(f);
                    }} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} className="text-xs"
                  value={form.notes ?? `Claim ${claim.claim_id} — ${pretty(claim.claim_type)} for ${claim.customer_name} on order ${claim.order_number}. Approved ${money(claim.approved_value)}.`}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="cn-notes-input" />
              </div>
            </div>
          )}

          {dialog === "emailcn" && (
            <div className="space-y-2">
              <Label className="text-xs">Customer Email</Label>
              <Input className="h-8 text-xs" value={form.to || ""} placeholder="customer@example.com"
                onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))} data-testid="email-cn-to" />
              <p className="text-[11px] text-zinc-500">
                The CN PDF goes with the email. The claim closes either way — if the email does not
                go out you will see an orange warning and you can send it by WhatsApp instead.
              </p>
            </div>
          )}

          {dialog === "marksent" && (
            <div className="space-y-2">
              <Label className="text-xs">How was it sent?</Label>
              <Input className="h-8 text-xs" value={form.method || ""} placeholder="Courier / WhatsApp / handed over"
                onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} data-testid="mark-sent-method" />
              <Label className="text-xs">Note</Label>
              <Textarea rows={2} className="text-xs" value={form.note || ""}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="mark-sent-note" />
            </div>
          )}

          {["late", "video", "videodone", "moreinfo", "resubmit", "reopen"].includes(dialog) && (
            <div className="space-y-2">
              <Label className="text-xs">
                {dialog === "moreinfo" ? "What proof do you need? (required)"
                  : dialog === "reopen" ? "Why open it again? (at least 10 letters)"
                  : dialog === "resubmit" ? "Your answer to Admin (required)" : "Note"}
              </Label>
              <Textarea rows={3} className="text-xs" value={form.note ?? form.reason ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value, reason: e.target.value }))}
                data-testid={`${dialog}-note`} />
            </div>
          )}

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setDialog(null)} data-testid="dialog-cancel">Cancel</Button>
            <Button size="sm" disabled={busy} data-testid="dialog-confirm" onClick={() => {
              if (dialog === "late") return call(() => api.post(`/claims/${claim._id}/late-override`, { decision: form.decision, note: form.note || "" }), "Late claim decided");
              if (dialog === "video") return call(() => api.post(`/claims/${claim._id}/request-video-call`, { note: form.note || "" }), "Video call requested");
              if (dialog === "videodone") return call(() => api.post(`/claims/${claim._id}/video-call-done`, { note: form.note || "" }), "Video call marked done");
              if (dialog === "reviewer") return call(() => api.post(`/claims/${claim._id}/reviewer-decision`, decisionBody()), "Decision saved");
              if (dialog === "final") return call(() => api.post(`/claims/${claim._id}/final-decision`, decisionBody()), "Final decision saved");
              if (dialog === "moreinfo") return call(() => api.post(`/claims/${claim._id}/final-decision`, { decision: "more_info", note: form.note || "" }), "Sent back to the reviewer");
              if (dialog === "resubmit") return call(() => api.post(`/claims/${claim._id}/resubmit-to-admin`, { note: form.note || "" }), "Resubmitted to Admin");
              if (dialog === "reopen") return call(() => api.post(`/claims/${claim._id}/reopen`, { reason: form.note || "" }), "Claim opened again");
              if (dialog === "emailcn") return call(() => api.post(`/claims/${claim._id}/email-cn`, { to: form.to }), "Claim closed — CN sent");
              if (dialog === "marksent") return call(() => api.post(`/claims/${claim._id}/mark-cn-sent`, { method: form.method, note: form.note }), "Claim closed — CN sent by hand");
              if (dialog === "cn") return call(() => api.post("/credit-notes", {
                cn_number: form.cn_number, cn_date: form.cn_date, customer_id: claim.customer_id,
                customer_name: claim.customer_name, amount: Number(form.amount ?? claim.approved_value),
                reason: `Claim ${claim.claim_id}`,
                notes: form.notes ?? `Claim ${claim.claim_id} — ${pretty(claim.claim_type)} for ${claim.customer_name} on order ${claim.order_number}. Approved ${money(claim.approved_value)}.`,
                attachments: form.attachments || [], claim_id: claim._id,
              }), "Credit note made — Admin will approve it");
              return null;
            }}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
